import { ScopeNote } from "@/components/ScopeNote";
import { useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { History } from "lucide-react";
import { HowItWorks } from "@/components/admin/HowItWorks";
import { SignalsFeed } from "@/components/radar/SignalsFeed";
import { Kpi, RadarCtx } from "@/components/radar/Bits";
import { JobDrawer, RunDialog } from "@/components/radar/RunJob";
import { Companies } from "@/components/radar/Companies";
import { CompanyPage } from "@/components/radar/CompanyPage";
import { People } from "@/components/radar/People";
import { PersonPage } from "@/components/radar/PersonPage";
import { Imports } from "@/components/radar/Imports";
import { Heatmap } from "@/components/radar/Heatmap";
import { RP, useQ } from "@/lib/radar";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { RADAR_MODULES, clientMods } from "@/lib/clients";
import { RadarSuite, ToolHeader, useTools } from "@/components/radar/RadarSuite";
import { IntelFromRadar } from "@/components/radar/SuiteLinks";
import { ConnectionsUpload } from "@/components/connections/ConnectionsUpload";
import { ConnectionsList } from "@/components/connections/ConnectionsList";
import { CampaignsBoard } from "@/components/outreach/CampaignsBoard";
import { ResourceTable } from "@/components/rcc/ResourceTable";
import { LinkedInSequences } from "@/components/rcc/li/LinkedInSequences";

const TABS = [["", "Companies"], ["people", "Decision Makers"], ["heatmap", "Heatmap"], ["imports", "Imports"], ["signals", "Signals feed"]];

const Header = ({ bump, openJob }) => {
  const s = useQ(`/admin/rcc/radar/summary?b=${bump}`);
  const d = s.data || {};
  const rj = d.running_job;
  return (
    <div className="space-y-2" data-testid="radar-summary">
      <div className="flex flex-wrap gap-2">
        <Kpi k="Companies" v={d.companies} testid="radar-kpi-companies" /><Kpi k="Ran" v={d.ran} testid="radar-kpi-ran" /><Kpi k="Hot" v={d.hot} testid="radar-kpi-hot" />
        <Kpi k="Act now" v={d.act_now} testid="radar-kpi-act-now" /><Kpi k="Decision makers" v={d.decision_makers} testid="radar-kpi-dms" />
        <Kpi k="New decision makers" v={d.new_decision_makers} dot={d.new_decision_makers > 0} testid="radar-kpi-new-dms" /><Kpi k="In a sequence" v={d.in_sequence} testid="radar-kpi-in-seq" />
      </div>
      {s.error && !s.missing && <p className="text-xs text-red-300">{s.error}</p>}
      {rj && <button onClick={() => openJob(rj.id || rj.job_id)} className="block w-full text-left" data-testid="radar-running-bar">
        <span className="flex justify-between text-[11px] text-[#F3C649]"><span>Running radar: {rj.done ?? 0}/{rj.total ?? 0}</span><span>View progress</span></span>
        <span className="mt-1 block h-1 overflow-hidden rounded-full bg-[#1f1f23]"><span className="block h-full bg-[#D4AF37] transition-[width]" style={{ width: `${rj.total ? (rj.done / rj.total) * 100 : 0}%` }} /></span>
      </button>}
    </div>
  );
};

export const OpportunityRadar = () => {
  const { client } = useAuth();
  const tabs = [...(client ? TABS.filter(([k]) => clientMods(client).radar.includes(RADAR_MODULES.find(([, , t]) => t === k)?.[0])) : TABS), ["connections", "My Connections"]];
  const sub = useParams()["*"] || "";
  const [run, setRun] = useState(null);
  const [job, setJob] = useState(null);
  const [bump, setBump] = useState(0);
  const refresh = () => setBump((b) => b + 1);
  const [seg, id] = sub.split("/");
  const tab = seg === "companies" ? "" : seg;
  const ctx = { openRun: setRun, openJob: setJob, bump, refresh };
  const guide = { "": "rcc-radar", people: "rcc-radar-people", heatmap: "rcc-radar-heatmap", imports: "rcc-radar-imports", signals: "rcc-radar-signals" }[tab];
  const tools = useTools();
  const tool = tools.find((t) => t.k === seg);
  const [ik, setIk] = useState(0);
  const toolBody = !tool ? null : tool.k === "campaigns" ? <CampaignsBoard /> : tool.k === "linkedin" ? <LinkedInSequences />
    : tool.k === "mpc" ? <ResourceTable name="mpcs" /> : <><IntelFromRadar onDone={() => setIk((x) => x + 1)} /><ResourceTable key={ik} name="intel" /></>;
  const body = tool ? <div className="space-y-4"><ToolHeader t={tool} /><HowItWorks key={`use-${tool.k}`} id={`use-${tool.k}`} />{toolBody}</div>
    : seg === "companies" && id ? <CompanyPage id={id} /> : seg === "people" && id ? <PersonPage id={id} />
    : tab === "connections" ? <div className="space-y-4"><HowItWorks id="use-connections-radar" /><ConnectionsUpload readOnly={false} onDone={refresh} /><ConnectionsList stamp={bump} /></div>
    : tab === "people" ? <People /> : tab === "heatmap" ? <Heatmap /> : tab === "imports" ? <Imports /> : tab === "signals" ? <SignalsFeed /> : <Companies />;
  return (
    <RadarCtx.Provider value={ctx}>
      <div className="space-y-5" data-testid="radar-module">
        <Header bump={bump} openJob={setJob} />
        <ScopeNote url="/admin/rcc/me" what="companies and people from your own workflows" testid="radar-scope-own" />
        {!id && <RadarSuite active={tool?.k} />}
        <div className="flex flex-wrap items-center gap-1 border-b border-[#27272A] pb-2" data-testid="radar-tabs">
          {tabs.map(([k, l]) => <NavLink key={k} to={k ? `${RP}/${k}` : RP} end className={cn("rounded-full px-3.5 py-1.5 text-xs transition-colors", tab === k && !id && !tool ? "bg-[#D4AF37] font-semibold text-black" : "text-zinc-400 hover:bg-[#18181B] hover:text-zinc-100")} data-testid={`radar-tab-${k || "companies"}`}>{l}</NavLink>)}
          <button onClick={() => setJob("history")} className="ml-auto flex items-center gap-1 text-[11px] text-zinc-500 hover:text-[#D4AF37]" data-testid="radar-job-history"><History className="h-3.5 w-3.5" />Recent runs</button>
          {!client && <Link to="/admin/cc/email-verification" className="text-[11px] text-zinc-500 hover:text-[#D4AF37]" data-testid="radar-ev-link">Email verification</Link>}
        </div>
        {!id && !tool && guide && guide !== "rcc-radar" && !client && <HowItWorks key={guide} id={guide} />}
        {body}
      </div>
      <RunDialog target={run} onClose={() => setRun(null)} openJob={setJob} />
      <JobDrawer jobId={job} setJobId={setJob} onDone={refresh} />
    </RadarCtx.Provider>
  );
};
